import { useEffect, useState } from "react";
import { LockKeyhole } from "lucide-react";

import {
    DEFAULT_CAPTURE_ONLY_MESSAGE,
    DEFAULT_READ_ONLY_MESSAGE,
    type WriteFenceMode,
} from "~/lib/read-only-mode";

interface ReadOnlyModeBannerProps {
    enabled: boolean;
    mode: WriteFenceMode | null;
    message: string | null;
    estimatedAvailableAt: string | null;
}

export function formatEstimatedAvailableAt(
    estimatedAvailableAt: string,
    locale?: string,
    timeZone?: string,
): string {
    return new Intl.DateTimeFormat(locale, {
        hour: "numeric",
        minute: "2-digit",
        timeZone,
        timeZoneName: "short",
    }).format(new Date(estimatedAvailableAt));
}

export function ReadOnlyModeBanner({
    enabled,
    mode,
    message,
    estimatedAvailableAt,
}: ReadOnlyModeBannerProps) {
    const [localEstimatedTime, setLocalEstimatedTime] = useState<string | null>(
        null,
    );

    useEffect(() => {
        // AIDEV-NOTE: Format after hydration so the ETA uses the viewer's timezone, not Vercel's.
        setLocalEstimatedTime(
            estimatedAvailableAt
                ? formatEstimatedAvailableAt(estimatedAvailableAt)
                : null,
        );
    }, [estimatedAvailableAt]);

    if (!enabled) return null;

    const isCaptureOnly = mode === "capture-only";

    return (
        <div
            role="status"
            aria-live="polite"
            className="fixed inset-x-0 top-0 z-[100] border-b border-amber-300 bg-amber-50 px-4 py-3 text-amber-950 shadow-sm"
            data-testid="read-only-mode-banner"
        >
            <div className="mx-auto flex max-w-5xl items-start gap-3">
                <LockKeyhole
                    aria-hidden="true"
                    className="mt-0.5 h-5 w-5 shrink-0"
                />
                <div>
                    <p className="text-base font-semibold">
                        {isCaptureOnly
                            ? "Parts Companion is completing a scheduled update."
                            : "Parts Companion is temporarily read-only."}
                    </p>
                    <p className="text-sm">
                        {isCaptureOnly && message === null ? (
                            <>
                                Parts Companion will be read-only{" "}
                                {localEstimatedTime && estimatedAvailableAt ? (
                                    <>
                                        until approximately{" "}
                                        <time dateTime={estimatedAvailableAt}>
                                            {localEstimatedTime}
                                        </time>
                                    </>
                                ) : (
                                    "for about 30 minutes"
                                )}
                                . {DEFAULT_CAPTURE_ONLY_MESSAGE}
                            </>
                        ) : (
                            message ?? DEFAULT_READ_ONLY_MESSAGE
                        )}
                    </p>
                </div>
            </div>
        </div>
    );
}
